import * as THREE from 'three';
import { createStoneMaterial } from '../../shaders/materials';
import type { InteractiveElement, TemporalUniforms } from '../../core/types';

export interface PlazaNarrativeResult {
  group: THREE.Group;
  interactables: InteractiveElement[];
  lanternsCount: number;
  update: (elapsed: number, temporal: number) => void;
}

/**
 * Builds the three narrative ground landmarks of Isidora:
 * 1. The Sunken Cockfight & Brawl Arena (ember-red lanterns & kinetic sparks)
 * 2. The Foreigner's Triple Loggia (three converging bridges & three luminous figures)
 * 3. The Wall of the Old Men (semicircular stone bench overlooking the city of memory)
 */
export function createIsidoraPlazaLandmarks(
  uniforms: TemporalUniforms
): PlazaNarrativeResult {
  const group = new THREE.Group();
  const interactables: InteractiveElement[] = [];
  let lanternsCount = 0;

  const piazzaStoneMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x3b2631,
    baseMemoryHex: 0x161f33,
    roughness: 0.78,
    metalness: 0.12,
  });

  const warmMarbleMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x7a5459,
    baseMemoryHex: 0x324361,
    roughness: 0.52,
    metalness: 0.18,
  });

  const brassInlayMat = new THREE.MeshStandardMaterial({
    color: 0xe3b15e,
    roughness: 0.25,
    metalness: 0.86,
  });

  // ---------------------------------------------------------------------------
  // 1. Central Stepped Piazza & Golden Logarithmic Spiral Pavement Inlay
  // ---------------------------------------------------------------------------
  const platformGeo = new THREE.CylinderGeometry(20.5, 23.0, 1.4, 64);
  const platform = new THREE.Mesh(platformGeo, piazzaStoneMat);
  platform.position.y = -0.7;
  platform.receiveShadow = true;
  group.add(platform);

  // Sunken octagonal arena steps in the center
  for (let s = 0; s < 3; s++) {
    const r = 6.2 - s * 0.9;
    const stepRingGeo = new THREE.RingGeometry(r - 0.85, r, 32);
    stepRingGeo.rotateX(-Math.PI / 2);
    const stepRing = new THREE.Mesh(stepRingGeo, warmMarbleMat);
    stepRing.position.y = 0.03 + (2 - s) * 0.14;
    stepRing.receiveShadow = true;
    group.add(stepRing);
  }

  // Logarithmic brass spiral inlaid into the piazza stones
  const floorSpiralPts: THREE.Vector3[] = [];
  for (let i = 0; i <= 200; i++) {
    const theta = (i / 200) * Math.PI * 6.5;
    const r = 0.45 * Math.exp(0.18 * theta);
    if (r > 19.5) break;
    floorSpiralPts.push(new THREE.Vector3(r * Math.cos(theta), 0.05, r * Math.sin(theta)));
  }
  const floorSpiralCurve = new THREE.CatmullRomCurve3(floorSpiralPts);
  const floorSpiralGeo = new THREE.TubeGeometry(floorSpiralCurve, 180, 0.06, 8, false);
  const floorSpiralMesh = new THREE.Mesh(floorSpiralGeo, brassInlayMat);
  group.add(floorSpiralMesh);

  // Ember Cockfight Arena Braziers & Kinetic Crimson Lanterns
  const arenaGroup = new THREE.Group();
  arenaGroup.position.set(-1.5, 0.2, 3.2);
  group.add(arenaGroup);

  const emberLanternMat = new THREE.MeshBasicMaterial({
    color: 0xff5a26,
  });
  const emberPointLight = new THREE.PointLight(0xff6b2b, 18, 22, 1.4);
  emberPointLight.position.set(0, 1.8, 0);
  arenaGroup.add(emberPointLight);

  const brazierPositions: THREE.Vector3[] = [];
  for (let b = 0; b < 8; b++) {
    const ang = (b / 8) * Math.PI * 2;
    const br = 3.2;
    const bx = Math.cos(ang) * br;
    const bz = Math.sin(ang) * br;

    const postGeo = new THREE.CylinderGeometry(0.08, 0.14, 1.35, 10);
    const post = new THREE.Mesh(postGeo, warmMarbleMat);
    post.position.set(bx, 0.68, bz);
    arenaGroup.add(post);

    const flameGeo = new THREE.OctahedronGeometry(0.22, 1);
    const flame = new THREE.Mesh(flameGeo, emberLanternMat);
    flame.position.set(bx, 1.48, bz);
    arenaGroup.add(flame);
    brazierPositions.push(flame.position);
    lanternsCount++;
  }

  // Twin kinetic duel sculptures in center of the sunken ring
  const duelPivot = new THREE.Group();
  duelPivot.position.y = 0.9;
  arenaGroup.add(duelPivot);

  for (const sign of [-1, 1]) {
    const crestGeo = new THREE.ConeGeometry(0.32, 0.95, 6);
    const crest = new THREE.Mesh(crestGeo, emberLanternMat);
    crest.position.set(sign * 0.75, 0.3, 0);
    crest.rotation.z = -sign * 0.48;
    duelPivot.add(crest);
  }

  // ---------------------------------------------------------------------------
  // 2. The Foreigner's Triple Loggia ("hesitating between two women always encounters a third")
  // ---------------------------------------------------------------------------
  const crossroadsGroup = new THREE.Group();
  crossroadsGroup.position.set(-7.5, 0.1, 7.8);
  group.add(crossroadsGroup);

  // Central hesitation rotunda platform
  const rotundaGeo = new THREE.CylinderGeometry(1.85, 2.1, 2.6, 24);
  const rotunda = new THREE.Mesh(rotundaGeo, warmMarbleMat);
  rotunda.position.y = 1.3;
  rotunda.castShadow = true;
  crossroadsGroup.add(rotunda);

  // Three converging arched bridges at 120-degree intervals, each with a luminous silhouette
  const womanColors = [0xffc978, 0xff94b8, 0x7fd4ff];
  const silhouetteMeshes: THREE.Mesh[] = [];

  for (let k = 0; k < 3; k++) {
    const angle = (k * Math.PI * 2) / 3 + 0.35;
    const bridgePivot = new THREE.Group();
    bridgePivot.rotation.y = angle;
    crossroadsGroup.add(bridgePivot);

    // Bridge span
    const spanGeo = new THREE.BoxGeometry(1.1, 0.28, 3.6);
    const span = new THREE.Mesh(spanGeo, warmMarbleMat);
    span.position.set(0, 2.45, 2.9);
    span.castShadow = true;
    bridgePivot.add(span);

    // Terminal pavilion column & arch
    const pavilionGeo = new THREE.CylinderGeometry(0.95, 1.1, 2.5, 16);
    const pavilion = new THREE.Mesh(pavilionGeo, piazzaStoneMat);
    pavilion.position.set(0, 1.25, 4.8);
    bridgePivot.add(pavilion);

    const archGeo = new THREE.TorusGeometry(0.72, 0.09, 10, 24, Math.PI);
    const arch = new THREE.Mesh(archGeo, brassInlayMat);
    arch.position.set(0, 3.2, 4.8);
    bridgePivot.add(arch);

    // Luminous sculptural figure beneath the arch
    const figMat = new THREE.MeshBasicMaterial({ color: womanColors[k] });
    const figGeo = new THREE.CapsuleGeometry(0.14, 0.72, 8, 12);
    const fig = new THREE.Mesh(figGeo, figMat);
    fig.position.set(0, 3.05, 4.8);
    bridgePivot.add(fig);
    silhouetteMeshes.push(fig);
    lanternsCount++;
  }

  // The Hesitating Foreigner figure at the center of the three bridges
  const foreignerMat = new THREE.MeshStandardMaterial({
    color: 0xf6d397,
    emissive: 0xd4883a,
    emissiveIntensity: 0.6,
    roughness: 0.3,
  });
  const foreignerGeo = new THREE.CapsuleGeometry(0.16, 0.82, 8, 12);
  const foreignerMesh = new THREE.Mesh(foreignerGeo, foreignerMat);
  foreignerMesh.position.set(0, 3.15, 0);
  crossroadsGroup.add(foreignerMesh);

  interactables.push({
    object: rotunda,
    kind: 'crossroads-arch',
    title: 'The Loggia of the Hesitating Foreigner',
    detail: 'Three arched bridges converge where the traveler hesitating between two encounters a third.',
    zhTitle: '猶豫異鄉人的三岔連廊',
    zhDetail: '三座拱橋在此交會——異鄉人在兩名女子間猶豫時，總會遇見第三位。',
    motif: 'crossroads',
    frequency: 349.23,
  });

  // ---------------------------------------------------------------------------
  // 3. The Wall of the Old Men ("In the square there is the wall where the old men sit...")
  // ---------------------------------------------------------------------------
  const wallGroup = new THREE.Group();
  wallGroup.position.set(11.5, 0.1, 13.0);
  wallGroup.rotation.y = Math.PI * 0.76;
  group.add(wallGroup);

  // Curved semicircular limestone parapet wall & bench
  const wallArcGeo = new THREE.CylinderGeometry(
    4.6,
    4.8,
    2.1,
    36,
    1,
    true,
    -Math.PI * 0.42,
    Math.PI * 0.84
  );
  const wallArc = new THREE.Mesh(wallArcGeo, warmMarbleMat);
  wallArc.material.side = THREE.DoubleSide;
  wallArc.position.y = 1.05;
  wallArc.castShadow = true;
  wallGroup.add(wallArc);

  const benchArcGeo = new THREE.CylinderGeometry(
    4.15,
    4.3,
    0.55,
    36,
    1,
    false,
    -Math.PI * 0.4,
    Math.PI * 0.8
  );
  const benchArc = new THREE.Mesh(benchArcGeo, piazzaStoneMat);
  benchArc.position.y = 0.28;
  wallGroup.add(benchArc);

  // Row of 7 seated elder silhouettes (including the aged Marco / traveler)
  const elderMat = new THREE.MeshStandardMaterial({
    color: 0x8ea8cf,
    emissive: 0x3b659c,
    emissiveIntensity: 0.45,
    roughness: 0.4,
  });

  for (let e = 0; e < 7; e++) {
    const ang = ((e - 3) / 3.5) * Math.PI * 0.34;
    const er = 4.22;
    const elderFig = new THREE.Group();
    elderFig.position.set(Math.sin(ang) * er, 0.85, Math.cos(ang) * er);
    elderFig.lookAt(0, 0.85, 0);

    const torso = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.15, 0.52, 8, 12),
      e === 3 ? foreignerMat : elderMat
    );
    elderFig.add(torso);
    wallGroup.add(elderFig);
  }

  // Soft lunar memory lantern above the Wall of the Old Men
  const memoryWallLight = new THREE.PointLight(0x70b5ff, 8, 18, 1.4);
  memoryWallLight.position.set(0, 3.2, 2.5);
  wallGroup.add(memoryWallLight);

  interactables.push({
    object: wallGroup,
    kind: 'memory-wall',
    title: 'The Wall of the Old Men',
    detail: '“He is seated in a row with them. Desires are already memories.” — Click to shift into Memory.',
    zhTitle: '老者之牆',
    zhDetail: '「他與他們並排坐在一起。慾望已經是記憶。」——點擊切換至記憶夜色。',
    motif: 'wall',
    frequency: 220.0,
  });

  const desireEmber = new THREE.Color(0xff5a26);
  const memoryEmber = new THREE.Color(0x5288d4);

  return {
    group,
    interactables,
    lanternsCount,
    update: (elapsed, temporal) => {
      // Cockfight arena flickers passionately in Desire, cools into memory embers in Old Age
      const flicker = 0.85 + 0.22 * Math.sin(elapsed * 9.5) * Math.cos(elapsed * 5.3);
      emberPointLight.intensity = THREE.MathUtils.lerp(20.0, 5.5, temporal) * flicker;
      emberPointLight.color.copy(desireEmber).lerp(memoryEmber, temporal);
      emberLanternMat.color.copy(desireEmber).lerp(memoryEmber, temporal * 0.85);

      duelPivot.rotation.y = elapsed * (2.4 - 1.8 * temporal);
      duelPivot.position.y = 0.9 + Math.abs(Math.sin(elapsed * 4.5)) * (0.32 * (1 - temporal * 0.7));

      // The hesitating foreigner turns slowly among the three bridges
      foreignerMesh.rotation.y = Math.sin(elapsed * 0.75) * 1.1;
      silhouetteMeshes.forEach((m, idx) => {
        m.position.y = 3.05 + Math.sin(elapsed * 1.5 + idx * 2.1) * 0.08;
      });

      // The Wall of the Old Men glows brighter as temporal state approaches Memory (1.0)
      memoryWallLight.intensity = THREE.MathUtils.lerp(4.0, 18.0, temporal);
      elderMat.emissiveIntensity = THREE.MathUtils.lerp(0.25, 0.95, temporal);
    },
  };
}
